import axios from 'axios';
import store from '../../redux/store';
import { jwtDecode } from 'jwt-decode';
import { logout, updateToken } from '../../redux/slices/authSlice';
import { resetWallet } from '../../redux/slices/walletSlice';
import { clearRememberMe, loadRememberMe, saveRememberMe } from '../remember';

// Function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;

  const decoded = jwtDecode(token);
  const currentTime = Date.now() / 1000;

  return decoded.exp < currentTime; // Return true if expired
};

const baseURL = process.env.REACT_APP_API_URL;
// Create an Axios instance
const api = axios.create({
  baseURL: baseURL,
});

let cachedIPDetails = null;

async function getIPDetails() {
  if (cachedIPDetails) return cachedIPDetails;
  try {
    const res = await fetch('https://ipwho.is/');
    const data = await res.json();
    if (data.success) {
      cachedIPDetails = data;
      return data;
    } else {
      throw new Error('Invalid IP response');
    }
  } catch (err) {
    console.error('Failed to fetch IP details:', err);
    return null;
  }
}


api.interceptors.request.use(
  async(config) => {
    const token = store.getState().auth?.token?.access;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`; 
    }

    // Default Content-Type for JSON
    config.headers['Content-Type'] =
      config.headers['Content-Type'] || 'application/json';

    config.headers['X-Time-Zone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // const ipData = await getIPDetails();
    // if (ipData) {
    //   config.headers['ngrok-skip-browser-warning'] = true || '';
    //   config.headers['X-User-IP'] = ipData.ip || '';
    //   config.headers['X-User-Country'] = ipData.country || '';
    //   config.headers['X-User-Country-Code'] = ipData.country_code || '';
    //   config.headers['X-User-City'] = ipData.city || '';
    //   config.headers['X-User-Timezone'] = ipData.timezone?.id || ipData.timezone || '';
    //   config.headers['X-User-ISP'] = ipData.connection?.isp || '';
    // }
    return config;
  },
  (error) => {
    console.log('api', error);
    Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const refreshToken = store.getState().auth?.token?.refresh;

    const errorDetail = error?.response?.data?.detail;
    const errorCode = error?.response?.data?.code;
    // Direct logout if user not found
    if (
      error?.response?.status === 401 &&
      (
        errorDetail === 'User not found' ||
        errorCode === 'user_not_found' ||
        errorDetail === 'Authentication credentials were not provided.'
      )
    ) {
      store.dispatch(logout());
      store.dispatch(resetWallet());
      clearRememberMe();
      window.location.href = '/signin';
      return Promise.reject(error);
    }

    // If the access token is expired (status 401) and we have a refresh token, try to refresh it
    if (
      error?.response?.status === 401 &&
      refreshToken &&
      !originalRequest._retry &&
      (
        errorDetail === 'Given token not valid for any token type' ||
        errorCode === 'token_not_valid'
      )
    ) {
      originalRequest._retry = true;

      if (isTokenExpired(refreshToken)) {
        // Refresh token is also expired, log out the user
        store.dispatch(logout());
        store.dispatch(resetWallet());
        clearRememberMe();
        window.location.href = '/signin';
        return Promise.reject(error);
      }

      try {
        // Request new access token using refresh token
        const response = await axios.post(`${baseURL}/token/refresh/`, {
          refresh: refreshToken,
        });

        const newAccessToken = response.data; // New access token from SimpleJWT
        const token = {
          ...newAccessToken,
          refresh: refreshToken,
        }
        store.dispatch(updateToken({ token: token })); // Update Redux with new token

        const remembered = loadRememberMe();
        if(remembered){
          const tokenUpdateRemeber = {...remembered, token: token}
          saveRememberMe(tokenUpdateRemeber)
        }

        // Retry the original request with the new access token
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return api(originalRequest); // Retry request with new token
      } catch (err) {
        // Refresh token is invalid, log out the user
        store.dispatch(logout());
        store.dispatch(resetWallet());
        clearRememberMe();
        window.location.href = '/signin';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
